import { GLOBAL_SEARCH_ENTITY_LABEL_ID } from 'components/global-search/entityDisplay';
import { Card } from 'components/ui/card';
import { ChevronDownIcon, CloseOutlined } from 'components/icons';
import { useMediaQuery } from '@uidotdev/usehooks';
import { useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import {
    GLOBAL_SEARCH_NARROW_QUERY,
    GlobalSearchFacetDimension,
    GlobalSearchFacetGroup,
    GlobalSearchUpdatedBucket,
} from 'types/globalSearch';
import { cn } from 'utils/cn';
import {
    clearFacetValues,
    FACET_DIMENSIONS,
    hasFacetSelection,
    isEntityType,
    selectedFacetValues,
    toggleFacetValue,
} from './searchFilters';

/**
 * How many values a group shows before the rest go behind "show more".
 *
 * The backend returns every value that has a match, and a dimension like "responsible" can run to
 * dozens on a broad query — enough to push the later groups off the screen entirely.
 */
const VISIBLE_VALUES = 5;

const FACET_LABEL_ID: Record<GlobalSearchFacetDimension, string> = {
    ENTITY_TYPE: 'global-search.facet.entity-type',
    OFFICE: 'global-search.facet.office',
    RESPONSIBLE: 'global-search.facet.responsible',
    PRACTICE_AREA: 'global-search.facet.practice-area',
    UPDATED: 'global-search.facet.updated',
};

/**
 * The buckets' own copy, which is ours and not the backend's: they are an enum, not a record, and
 * the label has to be translatable.
 */
const UPDATED_LABEL_ID: Record<GlobalSearchUpdatedBucket, string> = {
    TODAY: 'global-search.updated-bucket.today',
    THIS_WEEK: 'global-search.updated-bucket.this-week',
    THIS_MONTH: 'global-search.updated-bucket.this-month',
    THIS_YEAR: 'global-search.updated-bucket.this-year',
    OLDER: 'global-search.updated-bucket.older',
};

/**
 * What one facet value is called.
 *
 * Two of the dimensions are enums whose copy is this side's — a family reads the same here as it
 * does on the section heading above the dropdown's rows, and a bucket has no name at all until we
 * give it one. The other three are records, whose names the backend already sent.
 */
export function facetValueLabel(
    intl: IntlShape,
    dimension: GlobalSearchFacetDimension,
    value: string,
    facets: GlobalSearchFacetGroup[]
): string {
    if (dimension === 'ENTITY_TYPE' && isEntityType(value)) {
        return intl.formatMessage({ id: GLOBAL_SEARCH_ENTITY_LABEL_ID[value] });
    }
    if (dimension === 'UPDATED' && value in UPDATED_LABEL_ID) {
        return intl.formatMessage({ id: UPDATED_LABEL_ID[value as GlobalSearchUpdatedBucket] });
    }
    const group = facets.find((candidate) => candidate.dimension === dimension);
    return group?.values.find((candidate) => candidate.value === value)?.label ?? value;
}

/** The dimension a facet value belongs to, and what to show for it. */
export interface FacetRailProps {
    facets: GlobalSearchFacetGroup[];
    /** The results URL's parameters: what is selected, and what a toggle is applied to. */
    params: URLSearchParams;
    onChange: (next: URLSearchParams) => void;
}

/**
 * The rail that narrows the result list.
 *
 * Every count is the backend's, taken over the same match set the list is drawn from, and the
 * values keep the order they arrived in: a rail that reordered itself as counts moved would make
 * the value under the pointer a different one by the time it was clicked.
 *
 * Below the breakpoint it is not a rail but a fold above the rows. It is the only thing that can
 * APPLY a narrowing — the chips over the results can only take one off again — so dropping it there
 * would leave every facet unreachable on a phone; and leaving it open there would push the first
 * result off the screen. So it stays, closed, one row tall.
 */
export function FacetRail({ facets, params, onChange }: FacetRailProps) {
    const intl = useIntl();
    const [expanded, setExpanded] = useState<GlobalSearchFacetDimension[]>([]);
    const narrow = useMediaQuery(GLOBAL_SEARCH_NARROW_QUERY);
    const [open, setOpen] = useState(false);
    const showGroups = !narrow || open;

    const groups = FACET_DIMENSIONS.map((dimension) =>
        facets.find((group) => group.dimension === dimension && group.values.length > 0)
    ).filter((group) => group !== undefined);

    if (groups.length === 0) {
        return null;
    }

    const title = intl.formatMessage({ id: 'global-search.narrow' });

    return (
        <Card className="w-full flex-none gap-0 rounded-[20px] px-[18px] py-4 sm:sticky sm:top-0 sm:block sm:w-[252px]">
            <div className={cn('flex items-center justify-between gap-2', showGroups && 'mb-3.5')}>
                {narrow ? (
                    <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => setOpen((current) => !current)}
                        className="text-grey-700 flex min-h-11 items-center gap-1.5 text-sm leading-[22px] font-semibold"
                    >
                        {title}
                        <ChevronDownIcon aria-hidden className={cn('text-[16px]', open && 'rotate-180')} />
                    </button>
                ) : (
                    <span className="text-grey-700 text-sm leading-[22px] font-semibold">{title}</span>
                )}
                {hasFacetSelection(params) && (
                    <button
                        type="button"
                        className="text-link min-h-11 flex-none text-xs leading-[18px] hover:underline sm:min-h-0"
                        onClick={() => onChange(clearFacetValues(params))}
                    >
                        {intl.formatMessage({ id: 'global-search.clear-all' })}
                    </button>
                )}
            </div>
            {showGroups &&
                groups.map((group, index) => {
                    const selected = selectedFacetValues(params, group.dimension);
                    const isExpanded = expanded.includes(group.dimension);
                    const hidden = group.values.length - VISIBLE_VALUES;
                    const values = isExpanded ? group.values : group.values.slice(0, VISIBLE_VALUES);

                    return (
                        <div key={group.dimension} className={cn(index > 0 && 'border-divider mt-4 border-t pt-3.5')}>
                            <div className="text-grey-700 mb-1.5 text-xs leading-[18px] font-semibold">
                                {intl.formatMessage({ id: FACET_LABEL_ID[group.dimension] })}
                            </div>
                            <div className="flex flex-col gap-0.5">
                                {values.map((value) => {
                                    const on = selected.includes(value.value);
                                    return (
                                        <button
                                            key={value.value}
                                            type="button"
                                            aria-pressed={on}
                                            onClick={() =>
                                                onChange(toggleFacetValue(params, group.dimension, value.value))
                                            }
                                            className={cn(
                                                'hover:bg-grey-100 -mx-1.5 flex min-h-11 items-center justify-between gap-2 rounded-[6px] px-1.5 text-left text-xs leading-5 sm:min-h-0',
                                                on
                                                    ? 'bg-primary-surface-subtle text-primary font-semibold'
                                                    : 'text-grey-700'
                                            )}
                                        >
                                            <span className="truncate">
                                                {facetValueLabel(intl, group.dimension, value.value, facets)}
                                            </span>
                                            <span className="flex flex-none items-center gap-1.5">
                                                <span className="text-grey-500 font-normal">{value.count}</span>
                                                {on && <CloseOutlined aria-hidden className="text-[9px]" />}
                                            </span>
                                        </button>
                                    );
                                })}
                                {hidden > 0 && !isExpanded && (
                                    <button
                                        type="button"
                                        className="text-link min-h-11 w-fit text-xs leading-5 hover:underline sm:min-h-0"
                                        onClick={() => setExpanded((current) => [...current, group.dimension])}
                                    >
                                        {intl.formatMessage({ id: 'global-search.show-more' }, { count: hidden })}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
        </Card>
    );
}
